import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { acBase, Spinner, fmtDate, fmtDateTime, fmtTime, buildZoomLink, WAIcon, ADMIN_WA } from './academyUtils';
import ChangePasswordCard from './ChangePasswordCard';
import { getSubscriptionStatus, getDaysRemaining, getGraceDaysRemaining } from '../../utils/subscription';
import ProgressDashboard from '../ProgressDashboard';

// ── Circular SVG progress ────────────────────────────────────────────────────
function CircleProgress({ pct, remaining, total }) {
  const R       = 68;
  const circ    = 2 * Math.PI * R;
  const safe    = Math.max(0, Math.min(100, pct));
  const offset  = circ * (1 - safe / 100);
  const color   = pct > 50 ? '#27ae60' : pct > 20 ? '#e67e22' : '#e74c3c';
  return (
    <svg width={180} height={180} viewBox="0 0 180 180" style={{ display:'block', margin:'0 auto' }}>
      <circle cx={90} cy={90} r={R} fill="none" stroke="#e4ecf7" strokeWidth={14}/>
      <circle cx={90} cy={90} r={R} fill="none" stroke={color} strokeWidth={14}
        strokeDasharray={`${circ} ${circ}`} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 90 90)"
        style={{ transition:'stroke-dashoffset 0.8s ease' }}/>
      <text x={90} y={82} textAnchor="middle" fontSize={30} fontWeight={700} fill={color} fontFamily="'Plus Jakarta Sans',sans-serif">
        {Number(remaining).toFixed(1)}
      </text>
      <text x={90} y={102} textAnchor="middle" fontSize={12} fill="#9aa3b8" fontFamily="'Plus Jakarta Sans',sans-serif">hours left</text>
      <text x={90} y={118} textAnchor="middle" fontSize={10} fill="#c8d0e0" fontFamily="'Plus Jakarta Sans',sans-serif">of {Number(total).toFixed(0)} total</text>
    </svg>
  );
}

// ── Session status helpers ───────────────────────────────────────────────────
const SESSION_BADGE = {
  taught:                        { label:'Completed',  bg:'#e8f5ee', color:'#1a7a4a', border:'#c8ead8' },
  cancelled_by_student_excused:  { label:'Excused',    bg:'#f7f9fc', color:'#9aa3b8', border:'#dde3ed' },
  cancelled_by_student_charged:  { label:'Charged',    bg:'#fff8e8', color:'#b8620a', border:'#f0d890' },
  cancelled_by_teacher:          { label:'Rescheduled',bg:'#fff5f5', color:'#c0392b', border:'rgba(192,57,43,0.2)' },
  makeup:                        { label:'Make-up',    bg:'#e8f0fb', color:'#1a5fa8', border:'#c0d8f8' },
  manual_adjustment:             { label:'Adjustment', bg:'#f5ebff', color:'#5a1a8a', border:'#e0c8f8' },
};

const SUB_TYPE_LABEL = {
  'general-24h':    'General English · 24h/month',
  'ielts-standard': 'IELTS Standard',
  'ielts-premium':  'IELTS Premium · Unlimited',
  'standard':       'Standard',
  'premium':        'Premium',
  'trial':          'Trial',
  'custom':         'Custom',
  'general_english':'General English',
};

const IELTS_SUB_TYPES = new Set(['standard','premium','trial','ielts-standard','ielts-premium','ielts_standard','ielts_premium']);

// ── Component ────────────────────────────────────────────────────────────────
const SUB_LABEL_FRIENDLY = {
  'general-24h':    'General English · 24h/month',
  'ielts-standard': 'IELTS Standard',
  'ielts-premium':  'IELTS Premium',
  'general_english':'General English',
  'hourly':         'Hourly Package',
  'standard':       'IELTS Standard',
  'premium':        'IELTS Premium',
  'trial':          'Trial',
  'custom':         'Custom Programme',
};

export default function StudentDashboard({ student, onLogout, onPlacementTest, onAcademy, onCourses, onBook }) {
  const isIELTSStudent = IELTS_SUB_TYPES.has(student.subscription_type);
  const [subscriptions, setSubscriptions] = useState([]);
  const [bookings,      setBookings]      = useState([]);
  const [sessionsLog,   setSessionsLog]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [subRes, bkgRes] = await Promise.all([
      supabase.from('subscriptions')
        .select('*, teachers(id,name)')
        .eq('student_id', student.id)
        .eq('status', 'active'),
      supabase.from('bookings')
        .select('*, teachers(name,zoom_meeting_id), time_slots(start_time,end_time)')
        .eq('student_id', student.id)
        .order('created_at', { ascending: false })
        .limit(40),
    ]);
    let logRes = { data: [] };
    try {
      const r = await supabase.from('teacher_hours_log')
        .select('*, teachers(name)')
        .eq('student_id', student.id)
        .order('session_date', { ascending: false })
        .limit(30);
      logRes = r;
    } catch {}
    setSubscriptions(subRes.data || []);
    setBookings(bkgRes.data || []);
    setSessionsLog(logRes.data || []);
    setLoading(false);
  }, [student.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Hours aggregation ─────────────────────────────────────────────────────
  const totalHours     = subscriptions.reduce((s, x) => s + (Number(x.total_hours) || 0), 0);
  const usedHours      = subscriptions.reduce((s, x) => s + (Number(x.used_hours) || 0), 0);
  const remainingHours = Math.max(0, totalHours - usedHours);
  const pct            = totalHours > 0 ? (remainingHours / totalHours) * 100 : 0;
  const color          = pct > 50 ? '#27ae60' : pct > 20 ? '#e67e22' : '#e74c3c';
  const isLow          = pct < 20 && totalHours > 0;
  const isCritical     = pct < 10 && totalHours > 0;

  // Assigned teachers (deduplicated)
  const assignedTeachers = subscriptions
    .filter(s => s.teachers)
    .reduce((acc, s) => {
      if (!acc.find(t => t.id === s.teacher_id)) acc.push({ ...s.teachers, subType: s.subscription_type });
      return acc;
    }, []);

  // ── Upcoming sessions ─────────────────────────────────────────────────────
  const nowISO  = new Date().toISOString();
  const upcoming = bookings
    .filter(b => b.status === 'confirmed' && b.time_slots?.start_time > nowISO)
    .sort((a, b) => a.time_slots.start_time.localeCompare(b.time_slots.start_time))
    .slice(0, 3);

  // ── Cancellations this month ──────────────────────────────────────────────
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const cancellationsThisMonth = sessionsLog.filter(h =>
    h.session_date >= monthStart &&
    ['cancelled_by_student_excused', 'cancelled_by_student_charged'].includes(h.session_status)
  );
  const excusedCount  = cancellationsThisMonth.filter(h => h.session_status === 'cancelled_by_student_excused').length;
  const chargedCount  = cancellationsThisMonth.filter(h => h.session_status === 'cancelled_by_student_charged').length;
  const freeCancelLeft = Math.max(0, 1 - excusedCount);

  function renewWA() {
    const msg = `Hello Mr. Ahmed Salem, I need to renew my subscription. I have ${remainingHours.toFixed(1)} hours remaining. My name is ${student.name} and my WhatsApp is ${student.whatsapp}. Please advise on renewal options.`;
    window.open(`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(msg)}`, '_blank');
  }

  const SECTIONS = isIELTSStudent
    ? [['overview','📊 Overview'],['progress','📈 My Progress'],['history','📋 Sessions'],['cancellations','❌ Cancellations'],['account','⚙️ Account']]
    : [['overview','📊 Overview'],['history','📋 Sessions'],['cancellations','❌ Cancellations'],['account','⚙️ Account']];

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.65} }
        .pulse-warn { animation: pulse 2s ease-in-out infinite; }
      `}</style>

      {/* Top nav */}
      <div style={{ background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ background:'rgba(255,255,255,0.12)', borderRadius:8, padding:'4px 8px', display:'flex', alignItems:'center', flexShrink:0 }}>
            <img src="/logo.png" alt="IELTS Compass" style={{ height:30, objectFit:'contain' }}/>
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:16, color:'#fff', lineHeight:1.2 }}>Welcome back, {student.name}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginTop:2 }}>
              {SUB_LABEL_FRIENDLY[student.subscription_type] || student.subscription_type || 'General English'}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          {isIELTSStudent && onPlacementTest && (
            <button onClick={onPlacementTest} style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', color:'#fff', padding:'6px 12px', borderRadius:7, cursor:'pointer', fontSize:12 }}>
              📝 Placement Test
            </button>
          )}
          {onAcademy && (
            <button onClick={onAcademy} style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', color:'#fff', padding:'6px 12px', borderRadius:7, cursor:'pointer', fontSize:12 }}>
              🏫 Teachers
            </button>
          )}
          <button onClick={onLogout} style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', color:'#fff', padding:'6px 12px', borderRadius:7, cursor:'pointer', fontSize:12 }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Renewal banners */}
      {isCritical && (
        <div className="pulse-warn" style={{ background:'#c0392b', padding:'12px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
          <div style={{ fontSize:14, color:'#fff', fontWeight:600 }}>🚨 Only {remainingHours.toFixed(1)} hours left! Contact Mr. Ahmed to renew before your classes stop.</div>
          <button onClick={renewWA} style={{ background:'#25d366', color:'#fff', border:'none', padding:'8px 16px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700, display:'flex', alignItems:'center', gap:7 }}>
            <WAIcon size={14}/> Renew Now
          </button>
        </div>
      )}
      {isLow && !isCritical && (
        <div style={{ background:'#e67e22', padding:'11px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
          <div style={{ fontSize:13.5, color:'#fff', fontWeight:600 }}>⚠️ You have {remainingHours.toFixed(1)} hours remaining. Your hours are running low!</div>
          <button onClick={renewWA} style={{ background:'#25d366', color:'#fff', border:'none', padding:'7px 14px', borderRadius:7, cursor:'pointer', fontSize:12.5, fontWeight:700, display:'flex', alignItems:'center', gap:7 }}>
            <WAIcon size={13}/> Renew Hours
          </button>
        </div>
      )}

      <div style={{ maxWidth:800, margin:'0 auto', padding:'20px 16px' }}>
        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:60 }}><Spinner/></div>
        ) : (
          <>
            {/* ── SUBSCRIPTION EXPIRY CARD ── */}
            {(() => {
              const subStatus   = getSubscriptionStatus(student);
              const daysLeft    = getDaysRemaining(student);
              const graceDays   = getGraceDaysRemaining(student);
              if (!student.subscription_end) return null;
              if (subStatus === 'expired') return (
                <div style={{ background:'#fff0f0', border:'1.5px solid rgba(192,57,43,0.35)', borderRadius:12, padding:'16px 18px', marginBottom:14, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#8b1a1a', marginBottom:3 }}>🔴 Subscription Expired</div>
                    <div style={{ fontSize:12.5, color:'#c0392b' }}>Your subscription ended on {fmtDate(student.subscription_end)}. Contact Mr. Ahmed to renew.</div>
                  </div>
                  <button onClick={renewWA} style={{ ...acBase.btnWA, fontSize:12.5, padding:'8px 14px' }}><WAIcon size={13}/> Renew Now</button>
                </div>
              );
              if (subStatus === 'grace') return (
                <div style={{ background:'#fff8e8', border:'1.5px solid #f0d890', borderRadius:12, padding:'16px 18px', marginBottom:14, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#7a4a00', marginBottom:3 }}>⚠️ Grace Period — {graceDays} day{graceDays!==1?'s':''} remaining</div>
                    <div style={{ fontSize:12.5, color:'#b8620a' }}>Your subscription expired on {fmtDate(student.subscription_end)}. You have read-only access. Renew to book new sessions.</div>
                  </div>
                  <button onClick={renewWA} style={{ ...acBase.btnWA, fontSize:12.5, padding:'8px 14px' }}><WAIcon size={13}/> Renew</button>
                </div>
              );
              if (subStatus === 'active' && daysLeft !== null && daysLeft <= 7) return (
                <div style={{ background:'#fff8e8', border:'1.5px solid #f0d890', borderRadius:12, padding:'14px 18px', marginBottom:14, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#7a4a00', marginBottom:3 }}>🟡 Subscription expires in {daysLeft} day{daysLeft!==1?'s':''}</div>
                    <div style={{ fontSize:12.5, color:'#9a6a00' }}>Renew before {fmtDate(student.subscription_end)} to avoid interruption.</div>
                  </div>
                  <button onClick={renewWA} style={{ ...acBase.btnWA, fontSize:12.5, padding:'8px 14px' }}><WAIcon size={13}/> Renew Early</button>
                </div>
              );
              return null;
            })()}

            {/* ── MAIN HOURS CARD ── */}
            {subscriptions.length > 0 ? (
              <div style={{ background:'#fff', border:`2px solid ${color}33`, borderRadius:16, padding:'24px 24px 20px', boxShadow:'0 4px 20px rgba(26,42,74,0.09)', marginBottom:16 }}>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:16 }}>My Hours</div>

                <div style={{ display:'flex', gap:24, alignItems:'center', flexWrap:'wrap' }}>
                  {/* Circle */}
                  <div style={{ flexShrink:0 }}>
                    <CircleProgress pct={pct} remaining={remainingHours} total={totalHours}/>
                    <div style={{ textAlign:'center', fontSize:12, color:'#5a6a88', marginTop:6 }}>
                      {usedHours.toFixed(1)}h used of {totalHours.toFixed(0)}h total
                    </div>
                  </div>

                  {/* Subscription details */}
                  <div style={{ flex:1, minWidth:180 }}>
                    {subscriptions.map(sub => (
                      <div key={sub.id} style={{ background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:10, padding:'12px 14px', marginBottom:8 }}>
                        <div style={{ fontWeight:600, fontSize:13.5, color:'#1a2a4a', marginBottom:4 }}>
                          {SUB_TYPE_LABEL[sub.subscription_type] || sub.subscription_type}
                        </div>
                        {sub.teachers && (
                          <div style={{ fontSize:12.5, color:'#5a6a88', marginBottom:3 }}>
                            👩‍🏫 {sub.teachers.name}
                          </div>
                        )}
                        {sub.start_date && (
                          <div style={{ fontSize:12, color:'#9aa3b8' }}>Started: {fmtDate(sub.start_date)}</div>
                        )}
                        <div style={{ marginTop:8 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#9aa3b8', marginBottom:3 }}>
                            <span>Used</span><span>Remaining</span>
                          </div>
                          <div style={{ background:'#e4ecf7', borderRadius:4, height:6, overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${sub.total_hours>0?(sub.used_hours/sub.total_hours)*100:0}%`, background:color, borderRadius:4, transition:'width .6s' }}/>
                          </div>
                        </div>
                      </div>
                    ))}

                    {isLow && (
                      <button onClick={renewWA} style={{ width:'100%', background:'#25d366', color:'#fff', border:'none', padding:'11px 0', borderRadius:9, fontSize:13.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 2px 8px rgba(37,211,102,0.3)' }}>
                        <WAIcon size={14}/> Renew My Hours
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ ...acBase.card, background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', border:'none', marginBottom:16 }}>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#fff', marginBottom:6 }}>No Active Subscription</div>
                <div style={{ fontSize:13, color:'#b0c8e8', marginBottom:14 }}>Contact Mr. Ahmed to set up your subscription and get matched with a teacher.</div>
                <button onClick={renewWA} style={{ ...acBase.btnWA, width:'auto' }}><WAIcon size={14}/> Contact Mr. Ahmed</button>
              </div>
            )}

            {/* ── SUBSCRIPTION TYPE LABEL + QUICK ACTIONS ── */}
            <div style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:14, padding:'18px 20px', marginBottom:16, boxShadow:'0 1px 6px rgba(26,42,74,0.05)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:14 }}>
                <div>
                  <div style={{ fontSize:11, color:'#9aa3b8', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em', textTransform:'uppercase', marginBottom:4 }}>Your Programme</div>
                  <div style={{ fontSize:15, fontWeight:700, color:'#1a2a4a' }}>
                    {SUB_LABEL_FRIENDLY[student.subscription_type] || student.subscription_type || 'General English'}
                  </div>
                </div>
                <div style={{ fontSize:12, color:'#9aa3b8', background:'#f0f4f8', borderRadius:8, padding:'6px 12px', lineHeight:1.5, maxWidth:260 }}>
                  📅 Lessons are booked through WhatsApp. Contact Mr. Ahmed to schedule your sessions.
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <button onClick={() => window.open(`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent('Hello Mr. Ahmed Salem, I would like to contact you about my lessons.')}`, '_blank')}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', background:'#25d366', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer' }}>
                  <WAIcon size={13}/> Contact Mr. Ahmed
                </button>
                {onBook && (
                  <button onClick={onBook}
                    style={{ padding:'9px 14px', background:'#1a2a4a', border:'none', color:'#fff', borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:600 }}>
                    📅 Book a Session
                  </button>
                )}
                {isIELTSStudent && onPlacementTest && (
                  <button onClick={onPlacementTest}
                    style={{ padding:'9px 14px', background:'#f7f9fc', border:'1px solid #dde3ed', color:'#5a6a88', borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:500 }}>
                    📝 Placement Test
                  </button>
                )}
                {onAcademy && (
                  <button onClick={onAcademy}
                    style={{ padding:'9px 14px', background:'#f7f9fc', border:'1px solid #dde3ed', color:'#5a6a88', borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:500 }}>
                    🏫 View Teachers
                  </button>
                )}
                {onCourses && (
                  <button onClick={onCourses}
                    style={{ padding:'9px 14px', background:'#f7f9fc', border:'1px solid #dde3ed', color:'#5a6a88', borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:500 }}>
                    📚 View Courses
                  </button>
                )}
              </div>
            </div>

            {/* ── UPCOMING SESSIONS ── */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:10 }}>Upcoming Sessions</div>
              {upcoming.length === 0 ? (
                <div style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:10, padding:'20px', textAlign:'center' }}>
                  <div style={{ fontSize:13, color:'#9aa3b8', marginBottom:10 }}>No upcoming sessions scheduled.</div>
                  <button onClick={() => window.open(`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent('Hello Mr. Ahmed, I would like to book my next session. Please let me know what times are available.')}`, '_blank')}
                    style={{ ...acBase.btnWA, margin:'0 auto', width:'auto', padding:'9px 18px', borderRadius:8, fontSize:13 }}>
                    <WAIcon size={13}/> Contact Mr. Ahmed to Book
                  </button>
                </div>
              ) : (
                upcoming.map(b => {
                  const zoomLink = b.zoom_link || (b.teachers?.zoom_meeting_id ? buildZoomLink(b.teachers.zoom_meeting_id) : null);
                  return (
                    <div key={b.id} style={{ background:'#fff', border:'1.5px solid #c0d8f8', borderRadius:10, padding:'14px 18px', marginBottom:8, display:'flex', gap:14, alignItems:'center', flexWrap:'wrap' }}>
                      <div style={{ flex:1, minWidth:160 }}>
                        <div style={{ fontWeight:600, fontSize:14, color:'#1a2233', marginBottom:2 }}>{b.teachers?.name || 'Teacher'}</div>
                        <div style={{ fontSize:13, color:'#1a5fa8', fontWeight:600 }}>{b.time_slots?.start_time ? fmtDate(b.time_slots.start_time) : 'Date TBC'}</div>
                        <div style={{ fontSize:12, color:'#5a6a88' }}>{b.time_slots?.start_time ? fmtTime(b.time_slots.start_time) : ''} {b.time_slots?.end_time ? `– ${fmtTime(b.time_slots.end_time)}` : ''}</div>
                      </div>
                      {zoomLink && (
                        <a href={zoomLink} target="_blank" rel="noreferrer"
                          style={{ background:'#1a5fa8', color:'#fff', borderRadius:8, padding:'9px 16px', fontSize:13, fontWeight:700, textDecoration:'none', display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                          🎥 Join Zoom
                        </a>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* ── SECTION TABS ── */}
            <div style={{ display:'flex', borderBottom:'1px solid #dde3ed', marginBottom:16 }}>
              {SECTIONS.map(([k,l]) => (
                <button key={k} onClick={() => setActiveSection(k)}
                  style={{ border:'none', borderBottom:`2px solid ${activeSection===k?'#b8860b':'transparent'}`, background:'none', color:activeSection===k?'#b8860b':'#5a6a88', padding:'10px 16px', fontSize:13, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap' }}>
                  {l}
                </button>
              ))}
            </div>

            {/* ── SESSION HISTORY ── */}
            {activeSection === 'history' && (
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:12 }}>Session History</div>
                {sessionsLog.filter(h => h.session_status !== 'manual_adjustment').length === 0 ? (
                  <div style={{ color:'#9aa3b8', fontSize:13, padding:'16px 0', textAlign:'center' }}>No sessions recorded yet.</div>
                ) : (
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                      <thead>
                        <tr style={{ background:'#f7f9fc' }}>
                          {['Date','Teacher','Duration','Hours','Status'].map(h => (
                            <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.1em', textTransform:'uppercase', color:'#9aa3b8', borderBottom:'1px solid #dde3ed', whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sessionsLog.filter(h => h.session_status !== 'manual_adjustment').map(h => {
                          const bd = SESSION_BADGE[h.session_status] || SESSION_BADGE.taught;
                          return (
                            <tr key={h.id} style={{ borderBottom:'1px solid #f0f4f8' }}>
                              <td style={{ padding:'9px 12px', color:'#1a2233', whiteSpace:'nowrap' }}>{fmtDate(h.session_date)}</td>
                              <td style={{ padding:'9px 12px', color:'#1a2233' }}>{h.teachers?.name || '—'}</td>
                              <td style={{ padding:'9px 12px', color:'#5a6a88' }}>{Number(h.scheduled_duration).toFixed(1)}h</td>
                              <td style={{ padding:'9px 12px', fontWeight:600, color: h.hours_counted > 0 ? '#c0392b' : '#9aa3b8' }}>
                                {h.hours_counted > 0 ? `-${Number(h.hours_counted).toFixed(1)}h` : '—'}
                              </td>
                              <td style={{ padding:'9px 12px' }}>
                                <span style={{ fontSize:11, fontWeight:600, padding:'2px 9px', borderRadius:100, background:bd.bg, color:bd.color, border:`1px solid ${bd.border}` }}>
                                  {bd.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── OVERVIEW ── */}
            {activeSection === 'overview' && (
              <div>
                {/* Recent bookings */}
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:10 }}>My Classes</div>
                {bookings.filter(b => b.status !== 'pending').slice(0, 8).map(b => {
                  const zoomLink = b.zoom_link || (b.teachers?.zoom_meeting_id ? buildZoomLink(b.teachers.zoom_meeting_id) : null);
                  const bStyle = acBase.statusBadge(b.status);
                  return (
                    <div key={b.id} style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:9, padding:'12px 16px', marginBottom:7, display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                      <div style={{ flex:1, minWidth:160 }}>
                        <div style={{ fontWeight:600, fontSize:13.5, color:'#1a2233', marginBottom:1 }}>{b.teachers?.name || 'Teacher'}</div>
                        <div style={{ fontSize:12.5, color:'#5a6a88' }}>
                          {b.time_slots?.start_time ? fmtDateTime(b.time_slots.start_time) : 'Date TBC'}
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                        <span style={bStyle}>{b.status}</span>
                        {zoomLink && b.status === 'confirmed' && (
                          <a href={zoomLink} target="_blank" rel="noreferrer"
                            style={{ fontSize:12, color:'#1a5fa8', background:'#e8f0fb', border:'1px solid #c0d8f8', borderRadius:6, padding:'4px 10px', textDecoration:'none', fontWeight:600 }}>
                            🎥 Join Zoom
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── PROGRESS ── */}
            {activeSection === 'progress' && (
              <ProgressDashboard student={student}/>
            )}

            {/* ── CANCELLATIONS ── */}
            {activeSection === 'cancellations' && (
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:12 }}>My Cancellations This Month</div>

                {/* Free cancellations remaining */}
                <div style={{ background: freeCancelLeft > 0 ? '#e8f5ee' : '#fff5f5', border:`1px solid ${freeCancelLeft>0?'#c8ead8':'rgba(192,57,43,0.2)'}`, borderRadius:10, padding:'14px 18px', marginBottom:16 }}>
                  <div style={{ fontWeight:700, fontSize:14, color: freeCancelLeft>0?'#1a7a4a':'#c0392b', marginBottom:4 }}>
                    {freeCancelLeft > 0 ? `✓ ${freeCancelLeft} free cancellation remaining this month` : '✗ 0 free cancellations remaining this month'}
                  </div>
                  <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.6 }}>
                    You have 1 free cancellation per month with 48+ hours notice. After that, cancelled sessions still count against your hours.
                  </div>
                </div>

                {cancellationsThisMonth.length === 0 ? (
                  <div style={{ color:'#9aa3b8', fontSize:13, padding:'16px 0', textAlign:'center' }}>No cancellations this month. 🎉</div>
                ) : (
                  cancellationsThisMonth.map(h => {
                    const isCharged = h.session_status === 'cancelled_by_student_charged';
                    return (
                      <div key={h.id} style={{ background:'#fff', border:`1px solid ${isCharged?'#f0d890':'#dde3ed'}`, borderRadius:9, padding:'12px 16px', marginBottom:7, display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:600, fontSize:13.5, color:'#1a2233', marginBottom:1 }}>{h.teachers?.name || 'Class'}</div>
                          <div style={{ fontSize:12.5, color:'#5a6a88' }}>{fmtDate(h.session_date)} · {Number(h.scheduled_duration).toFixed(1)}h session</div>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
                          <span style={{ fontSize:11.5, fontWeight:700, padding:'3px 10px', borderRadius:100,
                            color: isCharged?'#b8620a':'#1a7a4a',
                            background: isCharged?'#fff8e8':'#e8f5ee',
                            border:`1px solid ${isCharged?'#f0d890':'#c8ead8'}` }}>
                            {isCharged ? 'Charged' : 'Excused ✓'}
                          </span>
                          {isCharged && <div style={{ fontSize:11, color:'#c0392b' }}>−{Number(h.hours_counted).toFixed(1)}h deducted</div>}
                        </div>
                      </div>
                    );
                  })
                )}

                <div style={{ background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:8, padding:'12px 16px', marginTop:8, fontSize:12.5, color:'#5a6a88', lineHeight:1.7 }}>
                  <strong style={{ color:'#1a2233' }}>Cancellation Policy:</strong><br/>
                  ✓ <strong>48+ hours notice (first time per month):</strong> Excused — no hours deducted.<br/>
                  ⚠️ <strong>Less than 48 hours / second+ cancellation:</strong> Charged — hours are deducted from your subscription.
                </div>
              </div>
            )}
            {/* ── ACCOUNT ── */}
            {activeSection === 'account' && (
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:16 }}>Account Settings</div>
                <ChangePasswordCard studentId={student.id}/>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
